import React, { useRef, useState } from "react";
import { Image, Upload, Trash2, Maximize2, AlignLeft, AlignCenter, AlignRight } from "lucide-react";

/**
 * Composant pour ajouter une image comme élément additionnel
 * L'image est stockée en base64 dans la config et sera responsive automatiquement
 *
 * @param {Object} config - Configuration de l'image
 * @param {Function} onConfigChange - Callback pour mettre à jour la config
 * @param {Function} onRemove - Callback pour supprimer l'élément
 * @param {boolean} isPreview - Mode aperçu seulement
 */
function ImageElement({ config, onConfigChange, onRemove, isPreview = false }) {
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // Configuration par défaut
    const defaultConfig = {
        imageUrl: null,         // URL R2 (après sauvegarde)
        imageData: null,        // Base64 de l'image (pendant édition)
        fileName: '',           // Nom du fichier original
        alt: '',               // Texte alternatif
        maxWidth: 'full',      // 'small' (300px), 'medium' (500px), 'large' (700px), 'full' (100%)
        alignment: 'center',   // 'left', 'center', 'right'
        caption: '',           // Légende sous l'image
    };

    const currentConfig = { ...defaultConfig, ...config };

    const updateConfig = (key, value) => {
        onConfigChange && onConfigChange({ ...currentConfig, [key]: value });
    };

    // Tailles disponibles
    const sizes = [
        { value: 'small', label: 'Petite', width: '300px' },
        { value: 'medium', label: 'Moyenne', width: '500px' },
        { value: 'large', label: 'Grande', width: '700px' },
        { value: 'full', label: 'Pleine largeur', width: '100%' },
    ];

    // Alignements disponibles
    const alignments = [
        { value: 'left', icon: AlignLeft, label: 'Gauche' },
        { value: 'center', icon: AlignCenter, label: 'Centre' },
        { value: 'right', icon: AlignRight, label: 'Droite' },
    ];

    // Fonction commune pour traiter un fichier image
    const processImageFile = (file) => {
        if (!file) return;

        // Vérifier le type de fichier
        if (!file.type.startsWith('image/')) {
            setError('Veuillez sélectionner une image valide (PNG, JPG, GIF)');
            return;
        }

        // Vérifier la taille (max 5 Mo)
        if (file.size > 5 * 1024 * 1024) {
            setError('L\'image ne doit pas dépasser 5 Mo');
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            // Convertir en base64
            const reader = new FileReader();
            reader.onload = (event) => {
                const imageData = event.target.result;

                // Créer une image pour obtenir les dimensions
                const img = new window.Image();
                img.onload = () => {
                    onConfigChange({
                        ...currentConfig,
                        imageData,
                        fileName: file.name,
                        originalWidth: img.width,
                        originalHeight: img.height,
                    });
                    setIsUploading(false);
                };
                img.src = imageData;
            };
            reader.onerror = () => {
                setError('Erreur lors de la lecture du fichier');
                setIsUploading(false);
            };
            reader.readAsDataURL(file);
        } catch (err) {
            setError('Erreur lors du chargement de l\'image');
            setIsUploading(false);
        }
    };

    // Gestion de l'upload d'image via input file
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        processImageFile(file);
    };

    // Gestion du drag & drop
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processImageFile(files[0]);
        }
    };

    // Supprimer l'image
    const handleRemoveImage = () => {
        onConfigChange({
            ...currentConfig,
            imageUrl: null,
            imageData: null,
            fileName: '',
            originalWidth: null,
            originalHeight: null,
            alt: '',
            caption: '',
        });
    };

    // Obtenir la classe CSS pour la taille
    const getSizeClass = () => {
        switch (currentConfig.maxWidth) {
            case 'small': return 'max-w-[300px]';
            case 'medium': return 'max-w-[500px]';
            case 'large': return 'max-w-[700px]';
            default: return 'max-w-full';
        }
    };

    // Obtenir la classe CSS pour l'alignement
    const getAlignmentClass = () => {
        switch (currentConfig.alignment) {
            case 'left': return 'mr-auto';
            case 'right': return 'ml-auto';
            default: return 'mx-auto';
        }
    };

    // Source de l'image : URL R2 en priorité, sinon base64
    const imageSrc = currentConfig.imageUrl || currentConfig.imageData;

    // Rendu de l'image responsive
    const renderImage = () => {
        if (!imageSrc) return null;

        return (
            <figure className={`${getAlignmentClass()} ${getSizeClass()} w-full`}>
                <img
                    src={imageSrc}
                    alt={currentConfig.alt || 'Image de la question'}
                    className="w-full h-auto rounded-lg shadow-md object-contain"
                    style={{ maxHeight: '60vh' }}
                />
                {currentConfig.caption && (
                    <figcaption className="text-center text-sm text-slate-500 mt-2 italic">
                        {currentConfig.caption}
                    </figcaption>
                )}
            </figure>
        );
    };

    // Mode aperçu
    if (isPreview) {
        if (!imageSrc) {
            return (
                <div className="p-4 bg-slate-100 rounded-xl border border-dashed border-slate-300 text-center">
                    <p className="text-slate-400 text-sm">Aucune image</p>
                </div>
            );
        }
        return (
            <div className="w-full">
                {renderImage()}
            </div>
        );
    }

    // Mode configuration
    return (
        <div className="border border-sky-200 rounded-xl overflow-hidden bg-white">
            {/* Zone d'upload ou aperçu */}
            {!imageSrc ? (
                <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg m-2 p-6 text-center cursor-pointer transition-all duration-200 ${
                        isDragOver
                            ? 'border-sky-500 bg-sky-100 scale-[1.02]'
                            : 'border-sky-300 hover:border-sky-400 hover:bg-sky-50/50'
                    }`}
                >
                    {isUploading ? (
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm text-sky-600">Chargement...</p>
                        </div>
                    ) : isDragOver ? (
                        <div className="flex flex-col items-center gap-2">
                            <Upload className="w-10 h-10 text-sky-500 mx-auto animate-bounce" />
                            <p className="text-sm font-bold text-sky-700">Déposez l'image ici !</p>
                        </div>
                    ) : (
                        <>
                            <Upload className="w-8 h-8 text-sky-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-sky-700">Glissez-déposez ou cliquez pour parcourir</p>
                            <p className="text-xs text-sky-400 mt-1">PNG, JPG, GIF (max 5 Mo)</p>
                        </>
                    )}
                </div>
            ) : (
                <>
                    {/* Aperçu de l'image */}
                    <div className="p-3 bg-sky-50/60 border-b border-sky-100">
                        {renderImage()}
                    </div>

                    {/* Actions sur l'image */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-sky-100">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-sky-100 text-sky-700 rounded-md text-xs font-medium hover:bg-sky-200 transition"
                        >
                            <Upload className="w-3.5 h-3.5" />
                            Changer
                        </button>
                        <button
                            onClick={handleRemoveImage}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 text-red-600 rounded-md text-xs font-medium hover:bg-red-100 transition"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                            Supprimer
                        </button>
                        {currentConfig.originalWidth && (
                            <span className="flex items-center gap-1 px-2 py-1 text-slate-400 text-[10px]">
                                <Maximize2 className="w-3 h-3" />
                                {currentConfig.originalWidth}×{currentConfig.originalHeight}
                            </span>
                        )}
                    </div>
                </>
            )}

            {/* Input fichier caché */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
            />

            {/* Message d'erreur */}
            {error && (
                <div className="mx-2 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-600">{error}</p>
                </div>
            )}

            {/* Options de configuration (seulement si image présente) */}
            {imageSrc && (
                <div className="px-3 py-2.5 space-y-2.5">
                    {/* Taille + Alignement sur une ligne */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-medium text-slate-400 uppercase">Taille</span>
                            <div className="flex gap-1">
                                {sizes.map(size => (
                                    <button
                                        key={size.value}
                                        onClick={() => updateConfig('maxWidth', size.value)}
                                        title={size.width}
                                        className={`px-2 py-1 rounded-md text-[10px] font-medium transition ${
                                            currentConfig.maxWidth === size.value
                                                ? 'bg-sky-500 text-white'
                                                : 'bg-gray-50 border border-gray-200 text-gray-500 hover:border-sky-300'
                                        }`}
                                    >
                                        {size.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-medium text-slate-400 uppercase">Align.</span>
                            <div className="flex gap-1">
                                {alignments.map(align => {
                                    const Icon = align.icon;
                                    return (
                                        <button
                                            key={align.value}
                                            onClick={() => updateConfig('alignment', align.value)}
                                            title={align.label}
                                            className={`p-1.5 rounded-md transition ${
                                                currentConfig.alignment === align.value
                                                    ? 'bg-sky-500 text-white'
                                                    : 'bg-gray-50 border border-gray-200 text-gray-500 hover:border-sky-300'
                                            }`}
                                        >
                                            <Icon className="w-3.5 h-3.5" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Alt text + Légende côte à côte */}
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={currentConfig.alt}
                            onChange={(e) => updateConfig('alt', e.target.value)}
                            placeholder="Texte alternatif..."
                            className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700 placeholder-gray-300 focus:border-sky-300 focus:outline-none transition"
                        />
                        <input
                            type="text"
                            value={currentConfig.caption}
                            onChange={(e) => updateConfig('caption', e.target.value)}
                            placeholder="Légende (optionnel)..."
                            className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700 placeholder-gray-300 focus:border-sky-300 focus:outline-none transition"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImageElement;
